import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

// Layouts
import AuthLayout from '@/layouts/AuthLayout';
import DashboardLayout from '@/layouts/DashboardLayout';

// Auth pages
import LoginPage from '@/pages/auth/LoginPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';

// Dashboard pages
import DashboardPage from '@/pages/dashboard/DashboardPage';
import TimeEntryPage from '@/pages/dashboard/TimeEntryPage';
import TasksPage from '@/pages/dashboard/TasksPage';
import ProfilePage from '@/pages/dashboard/ProfilePage';
import CheckInPage from '@/pages/dashboard/CheckInPage';
import InvoicesPage from '@/pages/dashboard/InvoicesPage';

// Admin pages
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminProjectsPage from '@/pages/admin/AdminProjectsPage';
import AdminReportsPage from '@/pages/admin/AdminReportsPage';
import AdminTimesheetsPage from '@/pages/admin/AdminTimesheetsPage';
import AdminTeamPage from '@/pages/admin/AdminTeamPage';
import AdminForceAccountPage from '@/pages/admin/AdminForceAccountPage';
import AdminProjectReportPage from '@/pages/admin/AdminProjectReportPage';
import AdminInvoicesPage from '@/pages/admin/AdminInvoicesPage';

// Components
import LoadingScreen from '@/components/LoadingScreen';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { loadUser, isLoading } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <AuthLayout>
              <ForgotPasswordPage />
            </AuthLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <AuthLayout>
              <ResetPasswordPage />
            </AuthLayout>
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-entry"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TimeEntryPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TasksPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ProfilePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/check-in"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CheckInPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <InvoicesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminDashboardPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminUsersPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminProjectsPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminReportsPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/timesheets"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminTimesheetsPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/team"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminTeamPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/force-account"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminForceAccountPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      {/* Project report routes */}
      <Route
        path="/admin/project-reports"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminProjectReportPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminInvoicesPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/project-reports/:slug"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <DashboardLayout>
                <AdminProjectReportPage />
              </DashboardLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
